import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { RadioStation } from '@/mocks/radios';
import { useAudioPlayer } from '@/context/AudioContext';
import { fetchCentovaMetadata } from '@/services/centovaMetadata';
import ShareModal from '@/components/feature/ShareModal';
import LikeButton from '@/components/feature/LikeButton';

/* ════════════════════════════════════════════════════════════════
   NOTE: OfficialHeroHeader — Radio Oficial de RadioFrecuenciaWeb
   ────────────────────────────────────────────────────────────────
   • La estación oficial de RadioFrecuenciaWeb (RFW Oficial)
     pertenece a la plataforma y será administrada exclusivamente
     desde el módulo Super Admin en futuras versiones.
   • No modificar permisos ni backend hasta que se implemente
     el panel administrativo de Super Admin.
   ════════════════════════════════════════════════════════════════ */

interface NowPlayingMeta {
  song: string | null;
  artist: string | null;
  title: string | null;
  listeners: number;
  status: 'online' | 'offline' | 'error';
  fetched_at: string;
}

interface OfficialHeroHeaderProps {
  station: RadioStation;
  followers: number;
  programsCount: number;
  hostsCount: number;
  onOpenCabina: () => void;
}

/* ── Accesos institucionales — diseño oscuro como la imagen ── */
const INSTITUTIONAL_ACCESS = [
  {
    label: 'Explora Más de 11,000 Radios',
    sub: 'Descubre estaciones de todo el mundo',
    icon: 'ri-earth-line',
    route: '/live',
    color: 'text-amber-400',
    bgIcon: 'bg-amber-400/10',
  },
  {
    label: 'Registra tu Radio',
    sub: 'Únete a la comunidad de radios más grande',
    icon: 'ri-radio-line',
    route: '/register-radio',
    color: 'text-sky-400',
    bgIcon: 'bg-sky-400/10',
  },
  {
    label: 'App Móvil RFW',
    sub: 'Escucha tus estaciones favoritas en cualquier lugar',
    icon: 'ri-smartphone-line',
    route: '/download-app',
    color: 'text-emerald-400',
    bgIcon: 'bg-emerald-400/10',
  },
  {
    label: 'Radios Destacadas',
    sub: 'Conoce las estaciones recomendadas',
    icon: 'ri-star-line',
    route: '/live',
    color: 'text-rose-400',
    bgIcon: 'bg-rose-400/10',
  },
];

/* ── Visualizador de ondas animado ── */
function WaveVisualizer({ isPlaying }: { isPlaying: boolean }) {
  const [heights, setHeights] = useState<number[]>(() =>
    Array.from({ length: 36 }, () => Math.max(12, Math.floor(Math.random() * 100)))
  );

  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      setHeights(
        Array.from({ length: 36 }, () => Math.max(12, Math.floor(Math.random() * 100)))
      );
    }, 120);
    return () => clearInterval(id);
  }, [isPlaying]);

  return (
    <div className="flex items-end gap-[2px] h-10 px-1 overflow-hidden">
      {heights.map((h, i) => (
        <div
          key={i}
          className="w-[3px] rounded-full bg-amber-400/80 shrink-0 transition-all duration-100 ease-linear"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}

export default function OfficialHeroHeader({ station, followers, programsCount, hostsCount, onOpenCabina }: OfficialHeroHeaderProps) {
  const { play, toggle, isPlaying, currentStation } = useAudioPlayer();
  const [following, setFollowing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [nowPlaying, setNowPlaying] = useState<NowPlayingMeta | null>(null);

  const isPlayingThis = isPlaying && currentStation?.id === station.id;

  /* ── Polling de Now Playing desde CentovaCast ── */
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    async function poll() {
      try {
        const meta = await fetchCentovaMetadata();
        if (!cancelled) setNowPlaying(meta);
      } catch {
        // Silencioso
      }
      if (!cancelled) timer = setTimeout(poll, 15000);
    }
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  /* ── Now playing label ── */
  const nowPlayingLabel = useMemo(() => {
    if (!nowPlaying || !nowPlaying.title) return null;
    if (nowPlaying.artist && nowPlaying.artist.trim() !== 'Unknown' && nowPlaying.artist.trim() !== '') {
      return { title: nowPlaying.title.trim(), artist: nowPlaying.artist.trim() };
    }
    return { title: nowPlaying.title.trim(), artist: null };
  }, [nowPlaying]);

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const handlePlayToggle = () => {
    if (isPlayingThis) {
      toggle();
    } else {
      play(station);
    }
  };
  const handleFollow = () => setFollowing(!following);

  const heroBgUrl = "https://readdy.ai/api/search-image?query=Professional%20broadcast%20studio%20institutional%20background%20with%20warm%20amber%20and%20dark%20charcoal%20tones%2C%20elegant%20radio%20station%20headquarters%20atmosphere%2C%20soft%20directional%20lighting%2C%20subtle%20geometric%20patterns%20of%20radio%20frequency%20waves%2C%20premium%20minimal%20corporate%20design%2C%20abstract%20sophisticated%20composition%2C%20clean%20architectural%20lines%2C%20professional%20media%20company%20aesthetic%2C%20no%20text%20or%20logos%2C%20wide%20panoramic&width=1440&height=520&seq=rfw-official-hero-v3&orientation=landscape";

  return (
    <>
      {/* ═══════════════ HERO INSTITUCIONAL ═══════════════ */}
      <div className="relative w-full overflow-hidden bg-[#0a0a0a]">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={heroBgUrl}
            alt="RadioFrecuenciaWeb Oficial"
            className="w-full h-full object-cover opacity-60"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40"></div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-8 pb-6">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">

            {/* ── LEFT: Hero Content ── */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-5 md:gap-6">

                {/* Logo circular grande */}
                <div className="shrink-0 flex flex-col items-center">
                  <div className="w-28 h-28 md:w-36 md:h-36 lg:w-44 lg:h-44 rounded-full overflow-hidden border-[3px] border-amber-500/80 shadow-2xl shadow-amber-500/10 ring-4 ring-black/50">
                    <img
                      src={station.logoUrl}
                      alt={station.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="mt-2 text-[11px] text-white/70 font-medium tracking-wide">RadioFrecuenciaWeb</p>
                  <p className="text-[11px] font-bold text-amber-400 tracking-[0.15em] uppercase">OFICIAL</p>
                </div>

                {/* Info principal */}
                <div className="flex-1 min-w-0 pt-1">

                  {/* Badge RADIO OFICIAL */}
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-300 text-[11px] font-bold uppercase tracking-wider mb-2">
                    <i className="ri-vip-crown-fill text-[10px]"></i>
                    RADIO OFICIAL
                  </div>

                  {/* Título + checkmark verificado */}
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-tight tracking-tight">
                      {station.name}
                    </h1>
                    <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#1d9bf0] flex items-center justify-center shrink-0 shadow-sm">
                      <i className="ri-check-line text-white text-xs md:text-sm"></i>
                    </div>
                  </div>

                  {/* Subtítulo: micrófono + RFW Oficial + badge */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <i className="ri-mic-2-fill text-white/60 text-sm"></i>
                    <span className="text-sm font-semibold text-white/90">RFW Oficial</span>
                    <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-white/60 text-[10px] border border-white/15 font-medium">
                      Emisora Oficial de la Plataforma
                    </span>
                  </div>

                  {/* Status: En Vivo · oyentes · Global */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-sm font-semibold text-emerald-400">En Vivo</span>
                    <span className="text-white/30">·</span>
                    <span className="text-sm text-white/70">{nowPlaying?.listeners ? nowPlaying.listeners.toLocaleString() : '128'} oyentes</span>
                    <span className="text-white/30">·</span>
                    <span className="text-sm text-white/70">Global</span>
                  </div>

                  {/* Descripción */}
                  <p className="text-sm text-white/50 leading-relaxed max-w-lg mb-5">
                    La voz oficial de RadioFrecuenciaWeb. Noticias, entrevistas, programas especiales, promoción de radios afiliadas y mucho más.
                  </p>

                  {/* Botones de acción */}
                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* Entrar a Cabina — principal interacción */}
                    <button
                      onClick={onOpenCabina}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20"
                    >
                      <i className="ri-mic-2-line"></i>
                      Entrar a Cabina
                    </button>

                    {/* Seguir */}
                    <button
                      onClick={handleFollow}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        following
                          ? 'bg-white/10 text-rose-400 border border-white/15 hover:bg-white/15'
                          : 'bg-white/10 text-white border border-white/15 hover:bg-white/15'
                      }`}
                    >
                      <i className={`${following ? 'ri-heart-3-fill' : 'ri-heart-3-line'}`}></i>
                      {following ? 'Siguiendo' : 'Seguir'}
                    </button>

                    {/* Compartir */}
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold text-sm border border-white/15 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-share-forward-line"></i>
                      Compartir
                    </button>

                    {/* Descargar App */}
                    <Link
                      to="/download-app"
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold text-sm border border-white/15 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-smartphone-line"></i>
                      Descargar App
                    </Link>
                  </div>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="mt-6 md:mt-8 flex flex-wrap items-center bg-black/40 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                <div className="flex-1 min-w-[100px] px-4 md:px-6 py-3 text-center border-r border-white/10">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-0.5 font-medium">Género</p>
                  <p className="text-sm font-semibold text-white/80">Variedad</p>
                </div>
                <div className="flex-1 min-w-[100px] px-4 md:px-6 py-3 text-center border-r border-white/10">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-0.5 font-medium">Idioma</p>
                  <p className="text-sm font-semibold text-white/80">Español</p>
                </div>
                <div className="flex-1 min-w-[100px] px-4 md:px-6 py-3 text-center border-r border-white/10">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-0.5 font-medium">Fundación</p>
                  <p className="text-sm font-semibold text-white/80">2020</p>
                </div>
                <div className="flex-1 min-w-[100px] px-4 md:px-6 py-3 text-center border-r border-white/10">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-0.5 font-medium">Tipo</p>
                  <p className="text-sm font-semibold text-white/80">Plataforma</p>
                </div>
                <div className="flex-1 min-w-[100px] px-4 md:px-6 py-3 text-center">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-0.5 font-medium">Calificación</p>
                  <div className="flex items-center gap-1 justify-center">
                    <span className="text-sm font-bold text-white/90">4.9</span>
                    <div className="flex text-amber-400 text-[10px] gap-0.5">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                    </div>
                    <span className="text-[10px] text-white/30 ml-0.5">(256)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── RIGHT: "Al Aire Ahora" Panel ── */}
            <div className="w-full lg:w-[340px] xl:w-[360px] shrink-0">
              <div className="bg-black/50 backdrop-blur-md rounded-2xl p-5 border border-white/10 shadow-2xl">

                {/* Header con línea dorada */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold text-white/90 uppercase tracking-wider">Al Aire Ahora</h3>
                  <div className="w-10 h-0.5 bg-amber-500 mt-1.5 rounded-full"></div>
                </div>

                {/* Station identity */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg overflow-hidden border border-white/10 shrink-0 bg-black/40">
                    <img src={station.logoUrl} alt={station.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] text-white/40">En cabina</p>
                    <p className="text-sm font-bold text-white truncate">RFW Oficial</p>
                  </div>
                </div>

                {/* Wave visualizer */}
                <WaveVisualizer isPlaying={isPlayingThis} />

                {/* Current song */}
                <div className="mt-3 mb-4">
                  <p className="text-[10px] font-bold text-amber-400 uppercase tracking-[0.12em] mb-1">Canción Actual</p>
                  {nowPlayingLabel ? (
                    <>
                      <p className="text-base font-bold text-white mb-0.5 truncate" title={nowPlayingLabel.title}>
                        {nowPlayingLabel.title}
                      </p>
                      {nowPlayingLabel.artist && (
                        <p className="text-xs text-white/40 truncate" title={nowPlayingLabel.artist}>
                          {nowPlayingLabel.artist}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-base font-bold text-white mb-0.5">Unidos por la Radio</p>
                      <p className="text-xs text-white/40">RFW Oficial</p>
                    </>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/8 text-white/50 text-[10px] border border-white/8">
                    <i className="ri-music-2-line text-[10px]"></i> AutoDJ
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/8 text-white/50 text-[10px] border border-white/8">
                    <i className="ri-time-line text-[10px]"></i> 02:35
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-400 text-[10px] font-semibold border border-emerald-500/20">
                    Calidad HD
                  </span>
                </div>

                {/* Next program */}
                <div className="border-t border-white/10 pt-3">
                  <p className="text-[10px] font-bold text-white/30 uppercase tracking-[0.12em] mb-2">Próximo Programa</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg overflow-hidden bg-white/10 shrink-0 border border-white/10">
                      <img src={station.logoUrl} alt="Próximo programa" className="w-full h-full object-cover opacity-80" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">Entrevistas RFW</p>
                      <p className="text-[11px] text-white/40">Hoy 2:00 PM - 3:00 PM</p>
                    </div>
                  </div>
                </div>

                {/* Play/Pause */}
                <button
                  onClick={handlePlayToggle}
                  className={`w-full flex items-center justify-center gap-2 mt-4 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap ${
                    isPlayingThis
                      ? 'bg-amber-500 text-black hover:bg-amber-400'
                      : 'bg-white/10 text-white hover:bg-white/15 border border-white/15'
                  }`}
                >
                  <i className={`text-base ${isPlayingThis ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
                  {isPlayingThis ? 'Pausar Transmisión' : 'Escuchar en Vivo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════ ACCESOS INSTITUCIONALES ═══════════════ */}
      <div className="relative bg-black/80 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {INSTITUTIONAL_ACCESS.map((item) => (
              <Link
                key={item.label}
                to={item.route}
                className="group flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/8 border border-white/8 hover:border-white/15 transition-all cursor-pointer"
              >
                <div className={`w-10 h-10 rounded-xl ${item.bgIcon} flex items-center justify-center shrink-0`}>
                  <i className={`${item.icon} ${item.color} text-lg`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white/90 truncate">{item.label}</p>
                  <p className="text-[11px] text-white/40 truncate">{item.sub}</p>
                </div>
                <i className="ri-arrow-right-s-line text-white/20 group-hover:text-white/50 transition-colors text-lg shrink-0"></i>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        entityName={station.name}
        entitySubtitle="RFW Oficial"
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
}